import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
  memo,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import ArrowDownIcon from './icons/arrowdown.svg?react'
import ArrowUpIcon from './icons/arrowup.svg?react'
import StopIcon from './icons/stop.svg?react'
import PaperClipIcon from './icons/paperclip.svg?react'
import GlobeIcon from './icons/globe.svg?react'

import ModelSelector from './ModelSelector.jsx';
import ThinkingModeSelector from './ThinkingModeSelector.jsx';
import { AppContext } from './AppContext.tsx';
import { useTabContext } from './TabContext.tsx';
import Logo from './logo/Logo.tsx';
import { AttachmentChip } from './AttachmentChip.jsx';
import { ScrollToBottomButton } from './ScrollToBottomButton.jsx';
import { IconButton } from './primitives/IconButton.jsx';
import { ImagePreviewModal } from './ImagePreviewModal.jsx';
import { isElectron } from '../utils/platform.js';

const MAX_TEXTAREA_PX = 160; // cap at ~7 lines

// File validation constants
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

const TextArea = ({
  sendMessage,
  anchored = false,
  showScrollToBottom,
  onScrollToBottom,
  isGenerating = false,
  onStopGeneration,
  hasContent = false,
  workingDirectory = null,
}) => {
  /* ───────────────────────────── context ───────────────────────────── */
  const {
    input: globalInput,
    files: globalFiles,
    addFiles: addGlobalFiles,
    removeFile: removeGlobalFile,
    onInputChange,
    mainInputRef,
    fileInputRef: contextFileInputRef,
    showConfirmation,
    models,
    selectedModel,
    selectedConversation,
  } = useContext(AppContext);

  // Get tab context for per-tab state isolation
  const { tab, updateTab } = useTabContext();

  // Use tab state if available, otherwise fallback to global state
  const input = tab ? (tab.input || '') : globalInput;
  const files = tab ? (tab.files || []) : globalFiles;

  // Tab-aware setters
  const setContextInput = useCallback(
    (value) => {
      if (tab) {
        updateTab({ input: value });
      } else {
        onInputChange(value);
      }
    },
    [tab, updateTab, onInputChange],
  );

  const addFiles = useCallback(
    (newFiles) => {
      if (tab) {
        const current = tab.files || [];
        // Transform files to match expected format
        const transformedFiles = newFiles.map(file => ({
          file,
          src: URL.createObjectURL(file),
          id: crypto.randomUUID(),
        }));
        updateTab({ files: [...current, ...transformedFiles] });
      } else {
        addGlobalFiles(newFiles);
      }
    },
    [tab, updateTab, addGlobalFiles],
  );

  const removeFile = useCallback(
    (id) => {
      if (tab) {
        updateTab({ files: (tab.files || []).filter((f) => f.id !== id) });
      } else {
        removeGlobalFile(id);
      }
    },
    [tab, updateTab, removeGlobalFile],
  );

  // Web search is now a global user preference, not per-tab/per-message

  const navigate = useNavigate();

  /* ─────────────────────── accurate one-line height ─────────────────── */
  const baseHeightRef = useRef(24); // fallback
  const [textareaHeight, setTextareaHeight] = useState('24px');
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);

  // Local state for fast typing (sync to context on submit)
  const [localInput, setLocalInput] = useState(input);

  // Image preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);

  // Ref for files container transition
  const filesContainerRef = useRef(null);
  
  // Refs map for file chips transitions
  const fileRefsMap = useRef(new Map());

  useEffect(() => {
    setLocalInput(input); // Sync from context/tab if it changes externally
  }, [input]);

  // Clear local input when tab or conversation changes
  useEffect(() => {
    if (tab) {
      setLocalInput(tab.input || '');
    } else {
      setLocalInput('');
    }
  }, [tab?.id, selectedConversation?.conversationId]);

  useEffect(() => {
    const ta = mainInputRef.current;
    if (!ta) return;

    /* put a dummy char, read natural height, then restore */
    const originalValue = ta.value;
    ta.value = 'X'; // any printable char
    ta.style.height = 'auto';
    const oneLine = ta.scrollHeight;
    ta.value = originalValue;

    baseHeightRef.current = oneLine;
    ta.style.height = `${oneLine}px`;
    setTextareaHeight(`${oneLine}px`);
  }, [mainInputRef]);

  /* ───────────────────── textarea grow / shrink logic ───────────────── */
  const handleTextareaChange = useCallback(() => {
    const ta = mainInputRef.current;
    if (!ta) return;

    if (ta.value.length === 0) { // collapse
      ta.style.height = `${baseHeightRef.current}px`;
      setTextareaHeight(`${baseHeightRef.current}px`);
      return;
    }

    ta.style.height = 'auto';
    const natural = ta.scrollHeight;
    const capped = Math.min(natural, MAX_TEXTAREA_PX);
    const finalH = Math.max(capped, baseHeightRef.current);

    ta.style.height = `${finalH}px`;
    setTextareaHeight(`${finalH}px`);
  }, [mainInputRef, baseHeightRef]); // Added refs as deps (stable, but explicit)

  /* ───────────────────────── keyboard shortcut ──────────────────────── */
  const handleShortcuts = useCallback(
    (e) => {
      if (e.metaKey && e.code === 'KeyG' && !e.shiftKey && !e.altKey) {
        if (showScrollToBottom) {
          e.preventDefault();
          onScrollToBottom();
        }
      }
    },
    [onScrollToBottom, showScrollToBottom],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleShortcuts);
    return () => document.removeEventListener('keydown', handleShortcuts);
  }, [handleShortcuts]);

  /* ────────────────────── drag-and-drop for files ───────────────────── */
  const [dragOverDropArea, setDragOverDropArea] = useState(false);
  const dragDepthRef = useRef(0);
  const isOverRef = useRef(false);

  const imageContainerDivRef = useRef(null);

  // File validation function
  // Electron with workspace: accept all file types (agent has filesystem tools)
  // Web: only accept JPEG/PNG images (sent as base64 to LLM providers)
  const hasWorkspace = Boolean(isElectron() && window.electronAPI?.workspace && workingDirectory);

  const validateFiles = useCallback((files) => {
    const invalidFiles = [];
    const validFiles = [];
    let needsWorkspace = false;

    files.forEach((file) => {
      const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
      const isValidType = hasWorkspace || isImage;
      const isValidSize = file.size <= MAX_FILE_SIZE;

      if (!isValidType) {
        if (isElectron()) {
          needsWorkspace = true;
        } else {
          invalidFiles.push(`${file.name}: Invalid file type. Only JPEG and PNG images are allowed.`);
        }
      } else if (!isValidSize) {
        invalidFiles.push(`${file.name}: File too large. Maximum size is ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB.`);
      } else {
        validFiles.push(file);
      }
    });

    if (needsWorkspace) {
      toast.error('Set a working directory in Settings to enable file uploads');
    }

    return { validFiles, invalidFiles };
  }, [hasWorkspace]);

  const handleFileChange = useCallback(
    (event) => {
      const selected = Array.from(event.target.files);
      const { validFiles, invalidFiles } = validateFiles(selected);

      if (invalidFiles.length > 0) {
        showConfirmation({
          title: 'Invalid Files',
          message: `Some files could not be added:\n\n${invalidFiles.join('\n')}`,
          confirmText: 'OK',
          variant: 'default',
          onConfirm: () => {}, // Just close the modal
        });
      }

      if (validFiles.length > 0) {
        addFiles(validFiles);
      }
    },
    [addFiles, validateFiles, showConfirmation], // Added showConfirmation
  );

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    dragDepthRef.current += 1;
    if (!isOverRef.current) {
      isOverRef.current = true;
      setDragOverDropArea(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0 && isOverRef.current) {
      isOverRef.current = false;
      setDragOverDropArea(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    // Required to allow drop
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      dragDepthRef.current = 0;
      if (isOverRef.current) {
        isOverRef.current = false;
        setDragOverDropArea(false);
      }

      const droppedFiles = Array.from(e.dataTransfer.files);
      const { validFiles, invalidFiles } = validateFiles(droppedFiles);

      if (invalidFiles.length > 0) {
        showConfirmation({
          title: 'Invalid Files',
          message: `Some files could not be added:\n\n${invalidFiles.join('\n')}`,
          confirmText: 'OK',
          variant: 'default',
          onConfirm: () => {},
        });
      }

      if (validFiles.length > 0) {
        addFiles(validFiles);
      }
    },
    [addFiles, validateFiles, showConfirmation],
  );

  /* listeners for input + drag/drop */
  useEffect(() => {
    const ta = mainInputRef.current;
    if (!ta) return;

    const onInput = () => requestAnimationFrame(handleTextareaChange);
    ta.addEventListener('input', onInput);

    const dropArea = imageContainerDivRef.current;
    const opts = { passive: false }; // dragover needs preventDefault

    dropArea?.addEventListener('dragenter', handleDragEnter, opts);
    dropArea?.addEventListener('dragleave', handleDragLeave, opts);
    dropArea?.addEventListener('dragover', handleDragOver, opts);
    dropArea?.addEventListener('drop', handleDrop, opts);

    return () => {
      ta.removeEventListener('input', onInput);
      dropArea?.removeEventListener('dragenter', handleDragEnter, opts);
      dropArea?.removeEventListener('dragleave', handleDragLeave, opts);
      dropArea?.removeEventListener('dragover', handleDragOver, opts);
      dropArea?.removeEventListener('drop', handleDrop, opts);
    };
  }, [handleTextareaChange, handleDragEnter, handleDragLeave, handleDragOver, handleDrop, mainInputRef]);

  /* ─────────────────────────── input events ─────────────────────────── */
  const onInputChangeEvent = useCallback(
    (e) => {
      setLocalInput(e.target.value); // Local update for fast typing
    },
    [],
  );

  const executeSendMessage = useCallback(() => {
    if (!localInput.trim() || isGenerating) return;

    // Check if no model is selected
    if (!selectedModel) {
      // Check if no models are available (no API keys configured)
      if (!models || models.length === 0) {
        toast.error('No AI models available. Please configure your API keys in settings.');
        return;
      }

      // Models exist but none selected
      toast.error('Please select an AI model before sending a message.');
      return;
    }

    // Proceed with sending (pass localInput directly)
    sendMessage(localInput);
    setLocalInput('');
    setContextInput(''); // Clear context (tab-aware)
    requestAnimationFrame(handleTextareaChange);
  }, [localInput, files.length, selectedModel, models, sendMessage, setContextInput, handleTextareaChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        executeSendMessage();
      } else if (e.key === 'Escape') {
        // Clear focus from input to allow keybindings
        e.preventDefault();
        e.target.blur();
      }
    },
    [executeSendMessage],
  ); // Dep on executeSendMessage (which has the volatiles)

  /* ───────────────────────── wheel behaviour ────────────────────────── */
  const handleContainerWheel = useCallback((e) => {
    const ta = mainInputRef.current;
    if (!ta) return;

    const canScroll = ta.scrollHeight > ta.clientHeight;
    if (!canScroll) return; // let chat scroll

    const delta = e.deltaY;
    const atTop = ta.scrollTop === 0;
    const atBottom = ta.scrollTop + ta.clientHeight === ta.scrollHeight;

    if ((delta < 0 && !atTop) || (delta > 0 && !atBottom)) {
      e.stopPropagation();
      ta.scrollTop += delta;
    }
  }, [mainInputRef]);

  /* ──────────────────────── image preview handlers ──────────────────────── */
  const handleImagePreviewClick = useCallback(({ src, alt }) => {
    setSelectedPreview({ src, alt });
    setShowPreviewModal(true);
  }, []);

  const handlePreviewModalClose = useCallback(() => {
    setSelectedPreview(null);
    setShowPreviewModal(false);
  }, []);

  /* ───────────────────────────── CSS helpers ─────────────────────────── */
  const containerClassName = useMemo(
    () => `
      w-full flex flex-col justify-center h-auto
      text-primary relative border-border-default box-border shadow-elevation-2
      border rounded-[16px] bg-bg-input-group
      transition-[background-color,border-color,box-shadow] duration-200
      ${isTextareaFocused ? 'border-accent' : ''}
    `,
    [isTextareaFocused],
  );

  /* ───────────────────────── files preview list ──────────────────────── */
  const filesList = useMemo(() => {
    if (!files.length) return null;
    return (
      <TransitionGroup
        component="div"
        className="flex flex-wrap gap-x-spacing-2 gap-y-spacing-2 px-spacing-3 pt-spacing-3"
      >
        {files.map((file) => {
          const isImage = file.file.type.startsWith('image/');
          
          // Get or create ref for this file
          if (!fileRefsMap.current.has(file.id)) {
            fileRefsMap.current.set(file.id, { current: null });
          }
          const nodeRef = fileRefsMap.current.get(file.id);
          
          return (
            <CSSTransition
              key={file.id}
              nodeRef={nodeRef}
              timeout={300}
              classNames={{
                enter: 'opacity-0 -translate-y-1',
                enterActive:
                  'opacity-100 translate-y-0 transition-all duration-300 ease-in-out',
                exit: 'opacity-100 translate-y-0',
              }}
            >
              <div ref={nodeRef} className="relative">
                <AttachmentChip
                  type={isImage ? 'image' : 'file'}
                  name={file.file.name}
                  url={file.src}
                  onRemove={() => removeFile(file.id)}
                  onPreview={
                    isImage
                      ? () => handleImagePreviewClick({
                          src: file.src,
                          alt: file.file.name || 'Attached image',
                        })
                      : undefined
                  }
                  context="input"
                />
              </div>
            </CSSTransition>
          );
        })}
      </TransitionGroup>
    );
  }, [files, removeFile, handleImagePreviewClick]); // Deps are stable

    /* ─────────────────────────────── render ───────────────────────────── */
  // console.log('TextArea render'); // Uncomment to debug re-renders
  return (
    <div
      ref={imageContainerDivRef}
      className={containerClassName}
      onWheel={handleContainerWheel}
    >
      {/* Drop overlay */}
      {dragOverDropArea && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[16px]
                    opacity-100 transition-opacity duration-200"
        >
          {/* Accent dashed ring */}
          <div className="absolute inset-0 rounded-[16px] border-2 border-dashed border-accent"></div>
          {/* Subtle elevation via color per DLS */}
          <div className="absolute inset-0 rounded-[16px] bg-bg-active/40"></div>
          {/* Center hint */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-spacing-2 px-spacing-3 py-spacing-2 rounded-lg bg-bg-card text-primary shadow-elevation-1">
              <PaperClipIcon className="size-4" />
              <span className="text-small">{hasWorkspace ? 'Drop files to attach' : 'Drop images to attach'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Thinking indicator */}
      {isGenerating && !hasContent && (
        <div
          className="absolute bottom-[110%] left-[12px] z-11 flex items-center"
          role="status"
          aria-live="polite"
        >
          <Logo 
            variant="animated" 
            text="Thinking" 
            size={14} 
            ariaLabel="Thinking"
            typingEnabled={false}
          />
        </div>
      )}

      {/* Scroll-to-bottom button */}
      {onScrollToBottom && (
        <div className="absolute bottom-[110%] right-0 z-11">
          <ScrollToBottomButton
            show={showScrollToBottom}
            onClick={onScrollToBottom}
          />
        </div>
      )}

      {/* Hidden arrow icons for compatibility - can be removed if not referenced elsewhere */}
      <div className="hidden">
        <ArrowDownIcon
          className="size-4 row-start-1 col-start-1 -translate-y-[150%]
                     transition-transform duration-200
                     ease-[cubic-bezier(0.785,0.135,0.15,0.86)]
                     group-hover:translate-y-0"
        />
        <ArrowDownIcon
          className="size-4 row-start-1 col-start-1
                       transition-transform duration-200
                       ease-[cubic-bezier(0.785,0.135,0.15,0.86)]
                       group-hover:translate-y-[150%]"
        />
      </div>

      {/* Files preview */}
      {files.length > 0 && (
        <CSSTransition
          in={files.length > 0}
          nodeRef={filesContainerRef}
          timeout={300}
          classNames={{
            enter: 'opacity-0',
            enterActive:
              'opacity-100 transition-opacity duration-300 ease-in-out',
            exit: 'opacity-100',
            exitActive:
              'opacity-0 transition-opacity duration-300 ease-in-out',
          }}
          unmountOnExit
        >
          <div ref={filesContainerRef}>
            {filesList}
          </div>
        </CSSTransition>
      )}

      <textarea
        rows={1}
        ref={mainInputRef}
        aria-label="Message input"
        aria-describedby="message-hint"
        className={`max-w-full outline-none resize-none overflow-auto bg-transparent rounded-[16px]
                   px-spacing-3 mt-spacing-3 placeholder:text-secondary max-h-[160px] ${isElectron() ? 'text-dls-body' : 'text-[16px] md:text-dls-body'}`}
        value={localInput} // Use local state
        onChange={onInputChangeEvent}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsTextareaFocused(true)}
        onBlur={() => setIsTextareaFocused(false)}
        placeholder="What's on your mind?"
        style={{ height: textareaHeight }}
        enterKeyHint="send"
        inputMode="text"
        autoCapitalize="sentences"
        autoCorrect="on"
        spellCheck={true}
      />
      <span id="message-hint" className="sr-only">Press Enter to send, Shift+Enter for new line</span>

      <div className="flex flex-row justify-between items-center w-full p-spacing-3">
        <div className="flex flex-row gap-spacing-2 items-center">
          <IconButton
            variant="bordered"
            size="md"
            aria-label="Attach files"
            onClick={() => contextFileInputRef.current.click()}
            className="text-primary"
          >
            <PaperClipIcon />
          </IconButton>
          <ThinkingModeSelector />
        </div>

        <input
          id="file-upload"
          ref={contextFileInputRef}
          type="file"
          multiple
          accept={hasWorkspace ? undefined : 'image/jpeg,image/png'}
          aria-label="Choose files to upload"
          onChange={handleFileChange}
          className="hidden"
        />

        <div className="relative z-100 flex flex-row gap-spacing-2 items-center">
          <ModelSelector position="top-right" isMain={true} />
          {isGenerating ? (
            <IconButton
              variant="accent"
              size="md"
              aria-label="Stop generation"
              onClick={onStopGeneration}
            >
              <StopIcon className="text-tertiary" />
            </IconButton>
          ) : (
            <IconButton
              variant="accent"
              size="md"
              disabled={!localInput.trim() && files.length === 0}
              aria-label="Send message"
              onClick={executeSendMessage}
            >
              <ArrowUpIcon className="text-tertiary" />
            </IconButton>
          )}
        </div>
      </div>

      <ImagePreviewModal
        isOpen={showPreviewModal}
        onClose={handlePreviewModalClose}
        src={selectedPreview?.src}
        alt={selectedPreview?.alt}
      />
    </div>
  );
};

TextArea.propTypes = {
  sendMessage: PropTypes.func.isRequired,
  anchored: PropTypes.bool,
  showScrollToBottom: PropTypes.bool,
  onScrollToBottom: PropTypes.func,
  isGenerating: PropTypes.bool,
  onStopGeneration: PropTypes.func,
  workingDirectory: PropTypes.string,
};

export default memo(TextArea);
